// EnneagramChart — all SVG path math lives here (pure functions, no React/DOM).
//
// ART DIRECTION (v2 — "sculpted flower" pass): each petal is a single closed,
// continuously-curved Catmull-Rom spline through 4 symmetric anchor points
// (hub → shoulder → tip → shoulder) — NOT a pie/donut segment, and NOT a
// rounded polygon (no straight edges at all, so no "corners" to round in the
// first place: continuous curvature everywhere = "polished river stone").
// The anchor set is mirror-symmetric about the petal's own radial axis, which
// gives perfectly soft, symmetric tangents at both hub and tip for free (see
// buildPetalPath below). The SHOULDER radius (the widest point) is a FIXED
// distance from the hub, independent of the tip's outerRadius — so a petal's
// WIDTH never grows with its value, only its LENGTH does (see SHOULDER_OFFSET).
// The angular slot is always exactly 360/n degrees, equally spaced, with a
// configurable gap — so every gap between petals is a rotated duplicate of
// every other (optically identical spacing, not just equal degrees).

import type { PetalSlot, Point } from "./types";

/** Viewbox is a fixed 200×200 unit space; the component scales it responsively. */
export const VIEWBOX = 200;
export const CENTER: Point = { x: VIEWBOX / 2, y: VIEWBOX / 2 };

/** Hub radius (petal base) and the maximum reachable tip radius, in viewBox units.
 * v33g — menší hub (bolo 30) → lupene idú bližšie k stredu, prázdny stred sa stiahne
 * na malú zaoblenú hviezdu (nie veľká diera). */
export const INNER_RADIUS = 18;
export const OUTER_RADIUS_MAX = 94;

/**
 * Fixed hub→shoulder distance (viewBox units), the SAME for every petal
 * regardless of its value. This is the one number that defines petal WIDTH —
 * because it never depends on outerRadius, width truly stays constant as a
 * petal grows longer (art-direction constraint: "longer, never wider").
 * ~34% of the full inner..outer range: enough to read as a proper "shoulder"
 * on a fully-grown petal without being so wide it dominates a short one.
 */
export const SHOULDER_OFFSET = 22;

/**
 * Only for petals short enough that the fixed shoulder would sit past (or too
 * close to) the tip: pulls the shoulder in proportionally so the outline never
 * inverts/self-crosses. `0.92` keeps the tip clearly the outermost point even
 * for the shortest realistic petal, at the cost of that one short petal being
 * slightly narrower than the others (unavoidable — a shoulder can't sit wider
 * than the petal is long).
 */
const SHOULDER_MAX_T = 0.92;

/**
 * Catmull-Rom tension for the petal outline. 1.0 = standard Catmull-Rom
 * (can overshoot into a "bulgier" flower-y curve); dialing this down softens
 * the bulge for a calmer, more restrained "Apple Health" curve instead of a
 * decorative one.
 */
const PETAL_TENSION = 0.82;

/**
 * A smooth interpolating spline naturally bulges slightly PAST its anchor
 * points (that's what makes it soft instead of faceted) — so a petal built
 * with its shoulder anchors exactly at the slot's half-angle would actually
 * render a hair wider than the slot, which starts to matter now that gaps
 * are intentionally small (art-direction: petals "almost touch"). This
 * pulls the shoulder anchors in by a fixed degree margin so the CURVE's
 * rendered edge — overshoot included — lands at (not past) the intended
 * half-angle. v33g — pri menšom INNER_RADIUS (18) je rameno na menšom polomere,
 * takže rovnaký absolútny presah = väčší uhlový presah (~3.6°). Margin zdvihnutý
 * 1.8°→4° (overené `no adjacent-petal overlap` testom pre n=3..9); žiadne prekrytie
 * susedných lupeňov pri žiadnom počte/hodnote, ktoré komponent podporuje.
 */
export const SHOULDER_ANGLE_MARGIN = 4;

export function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function clampValue(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, v));
}

/** Angle convention: 0° = straight up, increasing clockwise (matches SVG screen Y-down). */
export function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number): Point {
  const rad = toRad(angleDeg - 90);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/**
 * Equal angular slots for n petals (360/n each), petal visually occupying
 * (slot - gap) degrees centered in its slot — so only length ever changes,
 * never position/width of the slot itself.
 */
export function getPetalSlots(n: number, gapDeg: number): PetalSlot[] {
  if (n <= 0) return [];
  const slot = 360 / n;
  const halfAngle = Math.max(1, (slot - gapDeg) / 2);
  return Array.from({ length: n }, (_, index) => ({
    index,
    centerAngle: index * slot,
    halfAngle,
  }));
}

/**
 * Map a 0–100 value onto the tip radius. v33g — s MINIMÁLNOU dĺžkou lupeňa (`MIN_LEN`):
 * aj najmenšia kategória je dosť dlhá, aby sa rameno (SHOULDER_OFFSET) zmestilo → PLNÁ,
 * rovnaká šírka pre všetky lupene (žiadne „slivery"). Hodnota potom škáluje len ŠPIC
 * v kompaktnom rozsahu [inner+MIN_LEN .. outer] → vyvážený, súdržný kvet.
 */
const MIN_LEN = 40;
export function valueToOuterRadius(
  value: number,
  innerR: number = INNER_RADIUS,
  outerR: number = OUTER_RADIUS_MAX,
): number {
  const base = Math.min(innerR + MIN_LEN, outerR);
  return base + (outerR - base) * (clampValue(value) / 100);
}

function dist(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function lerpPoint(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/**
 * Generic rounded-polygon path builder: takes N vertices + a per-vertex corner
 * radius and returns a closed `d` path where every corner is replaced with a
 * cubic Bézier (superellipse-like — `smoothing` controls how "full"/organic
 * the rounding bulges vs. a tight circular corner). Straight edges between
 * corners stay straight lines. Reusable for any rounded organic shape.
 */
export function roundedPolygonPath(points: Point[], radii: number[], smoothing = 0.62): string {
  const n = points.length;
  if (n < 3) return "";
  const corners = points.map((v, i) => {
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];
    const dPrev = dist(v, prev);
    const dNext = dist(v, next);
    const r = Math.max(0, Math.min(radii[i] ?? 0, dPrev / 2, dNext / 2));
    const inPoint = dPrev > 0 ? lerpPoint(v, prev, r / dPrev) : v;
    const outPoint = dNext > 0 ? lerpPoint(v, next, r / dNext) : v;
    return { in: inPoint, out: outPoint, v };
  });

  let d = `M ${corners[0].in.x.toFixed(2)} ${corners[0].in.y.toFixed(2)} `;
  for (let i = 0; i < n; i++) {
    const c = corners[i];
    const c1 = lerpPoint(c.in, c.v, smoothing);
    const c2 = lerpPoint(c.out, c.v, smoothing);
    d += `C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)} ${c2.x.toFixed(2)} ${c2.y.toFixed(2)} ${c.out.x.toFixed(2)} ${c.out.y.toFixed(2)} `;
    const nextIn = corners[(i + 1) % n].in;
    d += `L ${nextIn.x.toFixed(2)} ${nextIn.y.toFixed(2)} `;
  }
  d += "Z";
  return d;
}

/**
 * Closed Catmull-Rom spline through `points`, converted to cubic Béziers
 * (uniform parameterization). Unlike `roundedPolygonPath` (straight edges
 * with rounded corners bolted on), this has NO straight segments at all —
 * the whole outline has continuous tangents everywhere, which is what gives
 * the petal its soft, "polished river stone" silhouette. `tension` < 1 pulls
 * the curve in slightly (calmer bulge, less overshoot) vs. standard
 * Catmull-Rom (`tension = 1`).
 */
export function catmullRomClosedPath(points: Point[], tension = 1): string {
  const n = points.length;
  if (n < 3) return "";
  const at = (i: number) => points[((i % n) + n) % n];
  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)} `;
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1x = p1.x + ((p2.x - p0.x) * tension) / 6;
    const c1y = p1.y + ((p2.y - p0.y) * tension) / 6;
    const c2x = p2.x - ((p3.x - p1.x) * tension) / 6;
    const c2y = p2.y - ((p3.y - p1.y) * tension) / 6;
    d += `C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} `;
  }
  return `${d}Z`;
}

export interface BuildPetalPathOptions {
  cx?: number;
  cy?: number;
  centerAngle: number;
  halfAngle: number;
  innerRadius?: number;
  outerRadius: number;
}

/**
 * Builds one petal's closed path from 4 anchor points — hub → shoulder-left →
 * tip → shoulder-right — joined by a single closed Catmull-Rom spline (see
 * `catmullRomClosedPath`). Because this anchor set is exactly mirror-symmetric
 * about the petal's own radial axis (shoulderLeft/Right mirror each other,
 * hub/tip sit on-axis), the spline's tangents at hub and tip come out purely
 * perpendicular to the axis automatically — a soft, symmetric, rounded base
 * and tip with zero extra rounding logic needed.
 *
 * The shoulder sits at a FIXED radius from the hub (see SHOULDER_OFFSET,
 * clamped only for very short petals via SHOULDER_MAX_T) — so only the tip
 * moves as `outerRadius` changes: the petal visibly grows/shrinks in LENGTH
 * only, never in WIDTH. Every petal shares the same SHOULDER_OFFSET/
 * SHOULDER_MAX_T/PETAL_TENSION, so all petals share identical curvature and
 * visual weight — the only thing that differs between petals is length.
 */
export function buildPetalPath(opts: BuildPetalPathOptions): string {
  const { cx = CENTER.x, cy = CENTER.y, centerAngle, halfAngle, innerRadius = INNER_RADIUS, outerRadius } = opts;
  const outerR = Math.max(innerRadius + 1, outerRadius);
  const shoulderR = Math.min(
    innerRadius + SHOULDER_OFFSET,
    innerRadius + (outerR - innerRadius) * SHOULDER_MAX_T,
  );
  // See SHOULDER_ANGLE_MARGIN: pull the shoulder anchors in slightly so the
  // spline's natural bulge (not the anchor itself) is what reaches the
  // slot's true edge — keeps adjacent petals from overlapping now that gaps
  // are small.
  const shoulderHalfAngle = Math.max(1, halfAngle - SHOULDER_ANGLE_MARGIN);

  const hub = polarToCartesian(cx, cy, innerRadius, centerAngle);
  const shoulderLeft = polarToCartesian(cx, cy, shoulderR, centerAngle - shoulderHalfAngle);
  const tip = polarToCartesian(cx, cy, outerR, centerAngle);
  const shoulderRight = polarToCartesian(cx, cy, shoulderR, centerAngle + shoulderHalfAngle);

  return catmullRomClosedPath([hub, shoulderLeft, tip, shoulderRight], PETAL_TENSION);
}

/** Hub/tip anchor points of a petal's axis — used to aim its depth gradient
 *  exactly along the petal's own radial direction (see EnneagramChart.tsx). */
export function getPetalAxisPoints(
  centerAngle: number,
  outerRadius: number,
  innerRadius: number = INNER_RADIUS,
): { hub: Point; tip: Point } {
  return {
    hub: polarToCartesian(CENTER.x, CENTER.y, innerRadius, centerAngle),
    tip: polarToCartesian(CENTER.x, CENTER.y, outerRadius, centerAngle),
  };
}

/** Anchor point for a petal's small number/value label (a bit inside the tip). */
export function getPetalLabelPoint(centerAngle: number, outerRadius: number, innerRadius = INNER_RADIUS): Point {
  const r = innerRadius + (outerRadius - innerRadius) * 0.86;
  return polarToCartesian(CENTER.x, CENTER.y, r, centerAngle);
}

/** Index of the highest value (ties → first occurrence). */
export function argmax(values: number[]): number {
  let best = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > values[best]) best = i;
  }
  return best;
}
